import { useCallback } from "react";
import apiClient from "src/api/axiosClient";
import { 
  EntityQRCodeParametersCreateBody,
  EntityQRCodeParametersSimpleView,
  EntityQRCodeParametersUpdateBody,
  QRCodesSearchParams,
  QRCodesSearchResponse
} from "../types";

export function useQRCodesApi() {
  return useCallback(
    () => ({
      searchQRCodes: (params: QRCodesSearchParams) => {
        params.type = params.type || undefined;
        return apiClient.get<QRCodesSearchResponse>('/qrcodes', { params });
      },


      createQRCode: (companyId: string, data: EntityQRCodeParametersCreateBody) => {
        return apiClient.post<EntityQRCodeParametersSimpleView>('/qrcodes', data, { params: { companyId } });
      },

      getQRCode: (id: string, companyId: string) => {
        return apiClient.get<EntityQRCodeParametersSimpleView>(`/qrcodes/${id}`, { params: { companyId } });
      },

      getKeywordQRCode: (keywordId: string, companyId: string) => {
        return apiClient.get<EntityQRCodeParametersSimpleView>(`/qrcodes/keywords/${keywordId}`, { params: { companyId } });
      },

      updateQRCode: (id: string, companyId: string, data: EntityQRCodeParametersUpdateBody) => {
        return apiClient.put<EntityQRCodeParametersSimpleView>(`/qrcodes/${id}`, data, { params: { companyId } });
      },

      archiveQRCode: (id: string, companyId: string) => {
        return apiClient.delete<void>(`/qrcodes/${id}`, { params: { companyId } });
      },

      getApplicationQRCodeParameters: (applicationId: string) => {
        return apiClient.get<EntityQRCodeParametersSimpleView>(`/qrcodes/applications/${applicationId}`);
      },

      setApplicationQRCodeParameters: (applicationId: string, parameters: { parameters: string }) => {
        return apiClient.put<EntityQRCodeParametersSimpleView>(`/qrcodes/applications/${applicationId}`, parameters);
      },

      getCompanyQRCodeParameters: (companyId: string) => {
        return apiClient.get<EntityQRCodeParametersSimpleView[]>(`/qrcodes/companies/${companyId}`);
      },

      createCompanyQRCodeParameters: (companyId: string, data: { name: string; parameters: string }) => {
        return apiClient.post<EntityQRCodeParametersSimpleView>(`/qrcodes/companies/${companyId}`, data);
      },

      updateCompanyQRCodeParameters: (companyId: string, parametersId: string, data: { name: string; parameters: string }) => {
        return apiClient.put<EntityQRCodeParametersSimpleView>(`/qrcodes/companies/${companyId}/${parametersId}`, data);
      },

      deleteCompanyQRCodeParameters: (companyId: string, parametersId: string) => {
        return apiClient.delete<void>(`/qrcodes/companies/${companyId}/${parametersId}`);
      },

    }),
    []
  );
}

// Individual hook exports for backward compatibility
export function useSearchQRCodesApi() {
  const api = useQRCodesApi();
  return useCallback(
    (params: QRCodesSearchParams) => api().searchQRCodes(params),
    [api]
  );
}


export function useCreateQRCodeApi() {
  const api = useQRCodesApi();
  return useCallback(
    (companyId: string, data: EntityQRCodeParametersCreateBody) => api().createQRCode(companyId, data),
    [api]
  );
}

export function useGetQRCodeApi() {
  const api = useQRCodesApi();
  return useCallback(
    (id: string, companyId: string) => api().getQRCode(id, companyId),
    [api]
  );
}

export function useGetKeywordQRCodeApi() {
  const api = useQRCodesApi();
  return useCallback(
    (keywordId: string, companyId: string) => api().getKeywordQRCode(keywordId, companyId),
    [api]
  );
}

export function useUpdateQRCodeApi() {
  const api = useQRCodesApi();
  return useCallback(
    (id: string, companyId: string, data: EntityQRCodeParametersUpdateBody) => api().updateQRCode(id, companyId, data),
    [api]
  );
}

export function useArchiveQRCodeApi() {
  const api = useQRCodesApi();
  return useCallback(
    (id: string, companyId: string) => api().archiveQRCode(id, companyId),
    [api]
  );
}

export function useGetApplicationQRCodeParametersApi() {
  const api = useQRCodesApi();
  return useCallback(
    (applicationId: string) => api().getApplicationQRCodeParameters(applicationId),
    [api]
  );
}

export function useSetApplicationQRCodeParametersApi() {
  const api = useQRCodesApi();
  return useCallback(
    (applicationId: string, parameters: { parameters: string }) => 
      api().setApplicationQRCodeParameters(applicationId, parameters),
    [api]
  );
}

export function useGetCompanyQRCodeParametersApi() {
  const api = useQRCodesApi();
  return useCallback(
    (companyId: string) => api().getCompanyQRCodeParameters(companyId),
    [api]
  );
}

export function useCreateCompanyQRCodeParametersApi() {
  const api = useQRCodesApi();
  return useCallback(
    (companyId: string, data: { name: string; parameters: string }) => 
      api().createCompanyQRCodeParameters(companyId, data),
    [api]
  );
}

export function useUpdateCompanyQRCodeParametersApi() {
  const api = useQRCodesApi();
  return useCallback(
    (companyId: string, parametersId: string, data: { name: string; parameters: string }) => 
      api().updateCompanyQRCodeParameters(companyId, parametersId, data),
    [api]
  );
}

export function useDeleteCompanyQRCodeParametersApi() {
  const api = useQRCodesApi();
  return useCallback(
    (companyId: string, parametersId: string) => 
      api().deleteCompanyQRCodeParameters(companyId, parametersId),
    [api]
  );
}
